import { alpha } from '@mui/material/styles';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { typographyClasses } from '@mui/material/Typography';
import { buttonBaseClasses } from '@mui/material/ButtonBase';
import { chipClasses } from '@mui/material/Chip';
import { iconButtonClasses } from '@mui/material/IconButton';
import { gray, red, green } from '../themePrimitives';


export const dataDisplayCustomizations = {
  MuiTableContainer: {
    styleOverrides: {
      root: ({ theme }) => ({
        width: '100%',
        overflowX: 'auto',
        backgroundColor: (theme.vars || theme).palette.background.default,
        backgroundImage: 'none',
        border: `1px solid ${(theme.vars || theme).palette.divider}`,
        borderRadius: (theme.vars || theme).shape.borderRadius,
        boxShadow: 'none',
      }),
    },
  },
  MuiTable: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        width: '100%',
        backgroundColor: (theme.vars || theme).palette.background.default,
        color: (theme.vars || theme).palette.text.primary,
      }),
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: (theme.vars || theme).palette.background.paper,
        [`& .MuiTableCell-root`]: {
          height: 46,
          padding: theme.spacing(0, 2),
          color: (theme.vars || theme).palette.text.secondary,
          backgroundColor: (theme.vars || theme).palette.background.paper,
          borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`,
          fontSize: theme.typography.pxToRem(12),
          fontWeight: 600,
          lineHeight: 1.25,
          letterSpacing: '0.02em',
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
        },
      }),
    },
  },
  MuiTableBody: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: (theme.vars || theme).palette.background.default,
        '& > .MuiTableRow-root:last-of-type > .MuiTableCell-root': {
          borderBottom: 0,
        },
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        transition: theme.transitions.create('background-color', {
          duration: 150,
        }),
        '&.MuiTableRow-hover:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.045),
        },
        '&:focus-within': {
          backgroundColor: alpha(theme.palette.primary.main, 0.055),
          boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
        },
        '&.Mui-selected': {
          backgroundColor: (theme.vars || theme).palette.action.selected,
          '&:hover': {
            backgroundColor: (theme.vars || theme).palette.action.selected,
          },
        },
        ...theme.applyStyles('dark', {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
          },
          '&:focus-within': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
          },
        }),
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(1.25, 2),
        color: (theme.vars || theme).palette.text.primary,
        borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`,
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 1.4,
        verticalAlign: 'middle',
        '& .MuiTypography-body2, & .MuiTypography-subtitle2': {
          fontSize: theme.typography.pxToRem(14),
          lineHeight: 1.35,
        },
        '& .MuiTypography-caption': {
          display: 'block',
          marginTop: theme.spacing(0.25),
          color: (theme.vars || theme).palette.text.secondary,
          fontSize: theme.typography.pxToRem(12),
          lineHeight: 1.3,
        },
        '& .MuiButton-root': {
          minWidth: 0,
          minHeight: 32,
          height: 32,
          padding: theme.spacing(0.5, 1.25),
          borderRadius: 8,
          boxShadow: 'none',
          fontSize: theme.typography.pxToRem(12),
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          '&:hover': {
            boxShadow: 'none',
          },
          '& .MuiButton-startIcon': {
            marginLeft: 0,
            marginRight: theme.spacing(0.625),
          },
          '& .MuiButton-endIcon': {
            marginLeft: theme.spacing(0.625),
            marginRight: 0,
          },
          '& .MuiSvgIcon-root': {
            fontSize: theme.typography.pxToRem(16),
          },
        },
        '& .MuiIconButton-root': {
          width: 32,
          height: 32,
          padding: theme.spacing(0.75),
          borderRadius: 8,
          '& .MuiSvgIcon-root': {
            fontSize: theme.typography.pxToRem(18),
          },
        },
        '& .MuiChip-root': {
          height: 23,
          maxHeight: 23,
          '& .MuiChip-label': {
            paddingRight: theme.spacing(0.875),
            paddingLeft: theme.spacing(0.875),
            fontSize: theme.typography.pxToRem(11.5),
            fontWeight: 600,
          },
          '& .MuiChip-icon': {
            fontSize: theme.typography.pxToRem(14),
          },
        },
        '& .MuiAvatar-root': {
          width: 40,
          height: 40,
          fontSize: theme.typography.pxToRem(14),
        },
        '& .MuiCheckbox-root': {
          padding: theme.spacing(0.5),
        },
      }),
      sizeSmall: ({ theme }) => ({
        padding: theme.spacing(1.25, 2),
      }),
      stickyHeader: ({ theme }) => ({
        backgroundColor: (theme.vars || theme).palette.background.paper,
        backgroundImage: 'none',
      }),
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`& .${svgIconClasses.root}`]: {
          width: '1rem',
          height: '1rem',
          color: (theme.vars || theme).palette.text.secondary,
        },
        [`& .${typographyClasses.root}`]: {
          fontWeight: 500,
        },
        [`& .${buttonBaseClasses.root}`]: {
          display: 'flex',
          gap: 8,
          padding: '2px 8px',
          borderRadius: (theme.vars || theme).shape.borderRadius,
          opacity: 0.7,
          '&.Mui-selected': {
            opacity: 1,
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
            [`& .${svgIconClasses.root}`]: {
              color: (theme.vars || theme).palette.text.primary,
            },
            '&:focus-visible': {
              backgroundColor: alpha(theme.palette.action.selected, 0.3),
            },
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.selected, 0.5),
            },
          },
          '&:focus-visible': {
            backgroundColor: 'transparent',
          },
        },
      }),
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: ({ theme }) => ({
        fontSize: theme.typography.body2.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.body2.lineHeight,
      }),
      secondary: ({ theme }) => ({
        fontSize: theme.typography.caption.fontSize,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: 'transparent',
        padding: '4px 8px',
        fontSize: theme.typography.caption.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 0,
      },
    },
  },
  MuiChip: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        border: '1px solid',
        borderRadius: '999px',
        [`& .${chipClasses.label}`]: {
          fontWeight: 600,
        },
        variants: [
          {
            props: {
              color: 'default',
            },
            style: {
              borderColor: gray[200],
              backgroundColor: gray[100],
              [`& .${chipClasses.label}`]: {
                color: gray[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: gray[500],
              },
              ...theme.applyStyles('dark', {
                borderColor: gray[700],
                backgroundColor: gray[800],
                [`& .${chipClasses.label}`]: {
                  color: gray[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: gray[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'success',
            },
            style: {
              borderColor: green[200],
              backgroundColor: green[50],
              [`& .${chipClasses.label}`]: {
                color: green[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: green[500],
              },
              ...theme.applyStyles('dark', {
                borderColor: green[800],
                backgroundColor: green[900],
                [`& .${chipClasses.label}`]: {
                  color: green[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: green[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'error',
            },
            style: {
              borderColor: red[100],
              backgroundColor: red[50],
              [`& .${chipClasses.label}`]: {
                color: red[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: red[500],
              },
              ...theme.applyStyles('dark', {
                borderColor: red[800],
                backgroundColor: red[900],
                [`& .${chipClasses.label}`]: {
                  color: red[200],
                },
                [`& .${chipClasses.icon}`]: {
                  color: red[300],
                },
              }),
            },
          },
          {
            props: { size: 'small' },
            style: {
              maxHeight: 20,
              [`& .${chipClasses.label}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
              [`& .${svgIconClasses.root}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
            },
          },
          {
            props: { size: 'medium' },
            style: {
              [`& .${chipClasses.label}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
            },
          },
        ],
      }),
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      actions: {
        display: 'flex',
        gap: 8,
        marginRight: 6,
        [`& .${iconButtonClasses.root}`]: {
          minWidth: 0,
          width: 36,
          height: 36,
        },
      },
    },
  },
  MuiIcon: {
    defaultProps: {
      fontSize: 'small',
    },
    styleOverrides: {
      root: {
        variants: [
          {
            props: {
              fontSize: 'small',
            },
            style: {
              fontSize: '1rem',
            },
          },
        ],
      },
    },
  },
};
